import React, { useState } from 'react';
import { X, Bell, SortAsc, SortDesc, CheckCircle2, Info, ChevronLeft } from 'lucide-react';
import './NotificationsModal.css';

const NotificationsModal = ({ isOpen, onClose, notifications, onMarkAsRead, formatDate }) => {
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest'
    const [selectedNotif, setSelectedNotif] = useState(null);

    if (!isOpen) return null;

    const getNotificationCategory = (notif) => {
        const subject = notif.title?.toLowerCase() || '';
        const msg = notif.content?.toLowerCase() || '';

        if (subject.includes('pagamento') || subject.includes('quota') || msg.includes('pagamento')) {
            return { icon: 'fas fa-file-invoice-dollar', color: 'blue', label: 'Pagamento' };
        }
        if (subject.includes('treino') || subject.includes('horário') || subject.includes('convocatória')) {
            return { icon: 'fas fa-whistle', color: 'blue', label: 'Treino' };
        }
        if (subject.includes('jogo') || subject.includes('partida')) {
            return { icon: 'fas fa-basketball-ball', color: 'blue', label: 'Jogo' };
        }
        return { icon: 'fas fa-bell', color: 'blue', label: 'Aviso' };
    };


    const sortedNotifications = [...notifications].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();

        // Handle invalid dates by moving them to the end
        if (isNaN(dateA) && isNaN(dateB)) return 0;
        if (isNaN(dateA)) return 1;
        if (isNaN(dateB)) return -1;

        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });



    const handleNotifClick = (notif) => {
        setSelectedNotif(notif);
        if (!notif.isRead) {
            onMarkAsRead(notif.id);
        }
    };

    const handleBackToList = () => {
        setSelectedNotif(null);
    };

    return (
        <div className="notifications-modal-overlay" onClick={onClose}>
            <div className="notifications-modal-container animate-in" onClick={(e) => e.stopPropagation()}>
                <header className="notifications-modal-header">
                    <div className="nm-header-left">
                        <button
                            className={`nm-back-btn ${selectedNotif ? 'is-visible' : ''}`}
                            onClick={handleBackToList}
                            aria-label="Voltar para a lista"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <div className="nm-header-icon">
                            <Bell size={24} />
                        </div>
                        <div className="nm-header-text">
                            <h2>Histórico de Notificações</h2>
                            <p>{notifications.length} mensagens no total</p>
                        </div>
                    </div>

                    <div className="nm-header-actions">
                        <button
                            className="nm-sort-btn"
                            onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                            title={sortOrder === 'newest' ? 'Mais antigas primeiro' : 'Mais recentes primeiro'}
                        >
                            {sortOrder === 'newest' ? <SortDesc size={20} /> : <SortAsc size={20} />}
                            <span>{sortOrder === 'newest' ? 'Recentes' : 'Antigas'}</span>
                        </button>
                        <button className="nm-close-btn" onClick={onClose}>
                            <X size={24} />
                        </button>
                    </div>
                </header>

                <div className={`notifications-modal-content ${selectedNotif ? 'has-selection' : ''}`}>
                    <div className="nm-list-section">
                        {sortedNotifications.length > 0 ? (
                            <div className="nm-scroll-area">
                                {sortedNotifications.map((notif) => {
                                    const category = getNotificationCategory(notif);
                                    const isActive = selectedNotif?.id === notif.id;

                                    return (
                                        <div
                                            key={notif.id}
                                            className={`nm-item ${!notif.isRead ? 'is-unread' : ''} ${isActive ? 'is-active' : ''} category-${category.color}`}
                                            onClick={() => handleNotifClick(notif)}
                                        >
                                            <div className="nm-item-indicator"></div>
                                            <div className="nm-item-content">
                                                <div className="nm-item-top">
                                                    <span className="nm-item-date">{formatDate(notif.createdAt)}</span>
                                                </div>
                                                <h4 className="nm-item-title">{notif.title}</h4>
                                                <p className="nm-item-preview">{notif.content}</p>
                                            </div>

                                            {!notif.isRead && <div className="nm-unread-dot"></div>}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="nm-empty-state">
                                <Bell size={48} className="nm-empty-icon" />
                                <p>Ainda não tens notificações no teu histórico.</p>
                            </div>
                        )}
                    </div>

                    <div className="nm-detail-section">
                        {selectedNotif ? (
                            <div className="nm-detail-view animate-in">
                                <div className="nm-detail-card glass-card">
                                    <div className="nm-detail-header">
                                        <div className="nm-detail-meta">
                                            <span className="nm-detail-date">
                                                {(() => {
                                                    const d = new Date(selectedNotif.createdAt);
                                                    return isNaN(d.getTime())
                                                        ? 'Data desconhecida'
                                                        : d.toLocaleString('pt-PT', {
                                                            day: '2-digit',
                                                            month: 'long',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        });
                                                })()}
                                            </span>

                                        </div>
                                        <h3 className="nm-detail-title">{selectedNotif.title}</h3>

                                        <div className="nm-detail-sender">
                                            <Info size={14} />
                                            <span>Enviado por: <strong>{selectedNotif.senderName || 'Sistema'}</strong></span>
                                        </div>
                                    </div>

                                    <div className="nm-detail-body">
                                        <p>{selectedNotif.content}</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="nm-detail-placeholder">
                                <Bell size={64} />
                                <h3>Seleciona uma notificação</h3>
                                <p>Clica numa notificação na lista para ler o conteúdo completo e ver detalhes do envio.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationsModal;
